import React, { useEffect, useState ,useRef} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, updateDoc, increment, addDoc, setDoc, deleteDoc,serverTimestamp } from 'firebase/firestore';
import { Share2,User, MapPin, BookOpen, Calendar, ChevronLeft, ChevronDown, FilePlus, Download, Bell, IndianRupee, Wallet, Search } from 'lucide-react';

// 🌟 CUSTOM SEARCHABLE DROPDOWN COMPONENT
const SearchableDropdown = ({ label, value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  // Close dropdown if user clicks outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter options based on what the user types
  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes(search.toLowerCase())
  );

  

  return (
    <div className="flex flex-col relative" ref={wrapperRef}>
      <label className="text-[10px] md:text-xs font-semibold text-gray-500 mb-1 ml-1">{label}</label>
      <div className="relative w-full">
        <input
          type="text"
          value={isOpen ? search : (value || '')}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
            if (e.target.value === '') onChange(''); // Clear filter if they delete text
          }}
          onFocus={() => { setIsOpen(true); setSearch(''); }}
          placeholder={placeholder}
          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs md:text-sm focus:ring-2 focus:ring-blue-400 outline-none text-gray-700 pr-8 cursor-text"
        />
        
        {/* Clear Button (X) or Chevron */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center z-10">
          {value && !isOpen ? (
            <button onClick={() => onChange('')} className="text-gray-400 hover:text-gray-800 text-lg font-bold leading-none">&times;</button>
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400 pointer-events-none" />
          )}
        </div>
      </div>

      {/* The Dropdown List */}
      {isOpen && (
        <div className="absolute top-[calc(100%+4px)] left-0 w-full bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-48 overflow-y-auto animate-fadeIn">
          <div 
            className="px-3 py-2 text-xs md:text-sm text-gray-400 hover:bg-gray-50 cursor-pointer border-b italic"
            onClick={() => { onChange(''); setIsOpen(false); }}
          >
            Clear selection...
          </div>
          {filteredOptions.length > 0 ? (
            filteredOptions.map(opt => (
              <div
                key={opt}
                onClick={() => { onChange(opt); setIsOpen(false); setSearch(''); }}
                className="px-3 py-2 text-xs md:text-sm text-gray-700 hover:bg-blue-50 cursor-pointer border-b last:border-0 truncate"
              >
                {opt}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-xs md:text-sm text-gray-400 text-center">No matches found</div>
          )}
        </div>
      )}
    </div>
  );
};

function CreatorProfile() {
  const { uid } = useParams(); // Gets the creator's ID from the URL
  const navigate = useNavigate();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const [creator, setCreator] = useState(null);
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const [lessonCount, setLessonCount] = useState(0);

  // --- SUBSCRIPTION STATES ---
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- NEW: CREATOR DISCOVERY STATES ---
  const [allCreators, setAllCreators] = useState([]);
  const [filterCity, setFilterCity] = useState('');
  const [filterSchool, setFilterSchool] = useState('');
  const [filterStandard, setFilterStandard] = useState('');
  const [filterName, setFilterName] = useState(''); // ✅ New State
  const [pauseScroll, setPauseScroll] = useState(false);

  // Creates a unique, persistent ID for unsigned visitors
  const getOrCreateGuestId = () => {
    let guestId = localStorage.getItem('eascode_guest_id');
    if (!guestId) {
      guestId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('eascode_guest_id', guestId);
    }
    return guestId;
  };

  // ✅ NEW: Add Marquee Animation Styles for the horizontal scroll
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes scrollLeftCreator {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      .mask-linear-gradient {
        mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
        -webkit-mask-image: -webkit-linear-gradient(left, transparent, black 5%, black 95%, transparent);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // ✅ NEW: Fetch all public profiles for the discovery directory
  useEffect(() => {
    const fetchAllCreators = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'public_profiles'));
        const creatorsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllCreators(creatorsData);
      } catch (error) {
        console.error("Error fetching all creators:", error);
      }
    };
    fetchAllCreators();
  }, []);

  const handleSubscribe = async () => {
    if (!auth.currentUser) {
      alert("Please sign in to subscribe to this creator!");
      return;
    }

    if (isSubmitting) return; 
    setIsSubmitting(true);

    const userUid = auth.currentUser.uid;
    const subRef = doc(db, 'subscriptions', `${userUid}_${uid}`);
    const creatorProfileRef = doc(db, 'public_profiles', uid);
    const historyRef = collection(db, 'subscription_history'); 

    try {
      if (isSubscribed) {
        await addDoc(historyRef, {
          subscriberId: userUid,
          creatorId: uid,
          action: 'unsubscribed',
          timestamp: new Date()
        });

        await deleteDoc(subRef);
        await updateDoc(creatorProfileRef, { subscriberCount: increment(-1) });
        
        setIsSubscribed(false);
        setSubscriberCount(prev => Math.max(0, prev - 1));

      } else {
        await setDoc(subRef, {
          subscriberId: userUid,
          creatorId: uid,
          timestamp: new Date()
        });

        await addDoc(historyRef, {
          subscriberId: userUid,
          creatorId: uid,
          action: 'subscribed',
          timestamp: new Date()
        });

        await updateDoc(creatorProfileRef, { subscriberCount: increment(1) });
        
        setIsSubscribed(true);
        setSubscriberCount(prev => prev + 1);
      }
    } catch (error) {
      console.error("Error toggling subscription:", error);
      alert("Failed to update subscription. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);

        const profileRef = doc(db, 'public_profiles', uid);
        const profileSnap = await getDoc(profileRef);
        if (profileSnap.exists()) {
          setCreator(profileSnap.data());
          setSubscriberCount(profileSnap.data().subscriberCount || 0); 
        } else {
            setCreator(null);
        }

        if (auth.currentUser) {
          const subRef = doc(db, 'subscriptions', `${auth.currentUser.uid}_${uid}`);
          const subSnap = await getDoc(subRef);
          setIsSubscribed(subSnap.exists());
        } else {
          setIsSubscribed(false); 
        }

        const uploadsRef = collection(db, 'uploads');
        const qWorksheets = query(uploadsRef, where('uid', '==', uid));
        const worksheetsSnap = await getDocs(qWorksheets);

        const formsData = worksheetsSnap.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            title: data.calculatedFileName || data.fileName || 'Untitled',
            uploadTime: data.uploadTime?.toDate ? data.uploadTime.toDate() : null,
          };
        });

        formsData.sort((a, b) => (b.uploadTime?.getTime?.() || 0) - (a.uploadTime?.getTime?.() || 0));
        setWorksheets(formsData);

        const lessonsRef = collection(db, 'Saved_lessons');
        const qLessons = query(lessonsRef, where('uid', '==', uid));
        const lessonsSnap = await getDocs(qLessons);
        setLessonCount(lessonsSnap.size);

      } catch (error) {
        console.error("Error fetching creator profile data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (uid) fetchAllData();
  }, [uid]);

  const handleDownload = async (form) => {
    const fileUrlToOpen = form.fileLink || form.fileUrl; 
    window.open(fileUrlToOpen, '_blank');

    try {
      const currentUser = auth.currentUser; 
      const downloaderId = currentUser ? currentUser.uid : getOrCreateGuestId();
      const downloaderRole = currentUser ? 'Registered User' : 'Public Guest';

      await addDoc(collection(db, 'creator_public_downloads'), {
        fileId: form.id,
        fileName: form.title,
        creatorId: form.uid || 'Unknown', 
        creatorName: form.uploadedBy || 'Admin',
        city: form.city || 'Unknown City',
        school: form.school || 'Unknown School',
        downloaderId: downloaderId,
        downloaderRole: downloaderRole,
        subject: form.subject || 'N/A',
        standard: form.standard || 'N/A',
        timestamp: new Date()
      });

      if (form.uid) {
        const creatorProfileRef = doc(db, 'public_profiles', form.uid);
        await updateDoc(creatorProfileRef, {
          totalDownloads: increment(1) 
        });
      }

      const worksheetRef = doc(db, 'uploads', form.id);
      await updateDoc(worksheetRef, {
        downloads: increment(1)
      });

      if (typeof setWorksheets !== 'undefined') {
        setWorksheets(prev => prev.map(f => f.id === form.id ? { ...f, downloads: (f.downloads || 0) + 1 } : f));
      }

    } catch (error) {
      console.error("Error logging public download:", error);
    }
  };

  // ✅ Security Check & Calculation for Monetization (ADMIN ONLY)
  const isOwnerOrAdmin = auth.currentUser && auth.currentUser.email === 'eascode.ltd@gmail.com';
  const totalDownloadsCount = creator?.totalDownloads || 0;
  const estimatedEarnings = lessonCount + worksheets.length + totalDownloadsCount; // 1 Rs each

// ✅ Compute filters for the Discovery Directory
  const uniqueCities = [...new Set(allCreators.map(c => c.city).filter(Boolean))].sort();
  const uniqueSchools = [...new Set(allCreators.map(c => c.school).filter(Boolean))].sort();
  const uniqueNames = [...new Set(allCreators.map(c => c.displayName).filter(Boolean))].sort(); // ✅ Get unique sorted names
  
  const filteredCreators = allCreators.filter(c => {
    const matchCity = filterCity ? c.city === filterCity : true;
    const matchSchool = filterSchool ? c.school === filterSchool : true;
    const matchStandard = filterStandard 
          ? (String(c.standard) === String(filterStandard) || c.supportedStandards?.includes(Number(filterStandard))) 
          : true;
    const matchName = filterName ? c.displayName === filterName : true; // ✅ Apply name filter
    return matchCity && matchSchool && matchStandard && matchName && c.id !== uid; 
  }).sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')); // ✅ Force Alphabetical Sort!

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-[Quicksand]">
      
      {/* HEADER */}
      <header className="w-full py-3 px-4 md:py-4 md:px-8 flex items-center bg-white shadow-sm sticky top-0 z-50">
        <button onClick={() => navigate('/Old_Home')} className="flex items-center text-gray-600 hover:text-blue-600 transition font-semibold text-sm mr-4">
          <ChevronLeft className="w-5 h-5" />
          <span className="hidden md:inline">Dashboard</span>
        </button>
        <h1 className="text-lg md:text-xl font-bold text-gray-800">Creator Storefront</h1>
      </header>

      <main className="flex-1 w-full max-w-7xl mx-auto px-0 md:px-8 py-4 md:py-8 animate-fadeIn">
        
        {/* 🌟 NEW: CREATOR DISCOVERY DIRECTORY */}
        <div className="bg-white mx-4 md:mx-0 rounded-2xl shadow-sm border border-blue-100 p-4 md:p-6 mb-8 relative overflow-hidden">
          
          <div className="flex items-center gap-2 mb-4">
            <Search className="w-5 h-5 text-blue-500" />
            <h3 className="text-sm md:text-lg font-bold text-gray-800 uppercase tracking-wide">Discover Creators</h3>
          </div>

{/* Search Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-5">
            
            <SearchableDropdown
              label="Creator Name"
              value={filterName}
              onChange={setFilterName}
              options={uniqueNames}
              placeholder="All Creators"
            />

            <SearchableDropdown
              label="City"
              value={filterCity}
              onChange={setFilterCity}
              options={uniqueCities}
              placeholder="All Cities"
            />

            <SearchableDropdown
              label="School"
              value={filterSchool}
              onChange={setFilterSchool}
              options={uniqueSchools}
              placeholder="All Schools"
            />

            {/* Standard Filter (Kept as normal select since it's just numbers) */}
            <div className="flex flex-col">
              <label className="text-[10px] md:text-xs font-semibold text-gray-500 mb-1 ml-1">Standard</label>
              <select 
                value={filterStandard} 
                onChange={e => setFilterStandard(e.target.value)} 
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs md:text-sm focus:ring-2 focus:ring-blue-400 outline-none text-gray-700 cursor-pointer"
              >
                <option value="">All Standards</option>
                {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>Standard {i+1}</option>)}
              </select>
            </div>

          </div>

          {/* Horizontal Marquee Scroll */}
          {filteredCreators.length > 0 ? (
            <div 
              className="overflow-hidden w-full mask-linear-gradient py-2"
              onMouseEnter={() => setPauseScroll(true)}
              onMouseLeave={() => setPauseScroll(false)}
            >
              <div 
                className="flex gap-4 w-max"
                style={{
                  // Multiply the number of creators by a fixed seconds-per-card value (e.g., 6 seconds)
                  animation: filteredCreators.length > 2 
                    ? `scrollLeftCreator ${filteredCreators.length * 6}s linear infinite` 
                    : 'none',
                  animationPlayState: pauseScroll ? 'paused' : 'running',
                }}
              >
                {/* Double the array for seamless infinite looping if needed */}
                {(filteredCreators.length > 2 ? [...filteredCreators, ...filteredCreators] : filteredCreators).map((c, idx) => (
                  <div
                    key={`${c.id}-${idx}`}
                    onClick={() => {
                      navigate(`/CreatorProfile/${c.id}`);
                      window.scrollTo(0, 0); // Scroll to top to see new hero card
                    }}
                    className="w-[260px] md:w-[300px] bg-gradient-to-r from-blue-50 to-indigo-50/50 border border-blue-100 hover:border-blue-400 hover:shadow-md transition-all p-3 rounded-xl cursor-pointer flex items-center gap-3 shrink-0"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white border border-blue-200 shrink-0 overflow-hidden flex items-center justify-center shadow-sm">
                      {c.avatarUrl ? (
                        <img src={c.avatarUrl} alt={c.displayName} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-blue-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-xs md:text-sm text-gray-900 truncate">{c.displayName}</h4>
                      <p className="text-[9px] md:text-[10px] text-gray-500 truncate flex items-center gap-1">
                        <MapPin className="w-2.5 h-2.5" /> {[c.school, c.city].filter(Boolean).join(', ')}
                      </p>
                    {(c.supportedStandards?.length > 0 || c.standard) && (
                      <p className="text-[9px] md:text-[10px] font-bold text-blue-600 mt-0.5 uppercase tracking-wide">
                        Supports Std: {c.supportedStandards?.length > 0 ? c.supportedStandards.sort((a,b) => a - b).join(', ') : c.standard}
                      </p>
                    )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <p className="text-xs md:text-sm text-gray-500 font-medium">No other creators found matching these filters.</p>
            </div>
          )}
        </div>


{/* CREATOR HERO CARD & CONTENT */}
        {loading ? (
          <div className="py-20 flex flex-col justify-center items-center mx-4 md:mx-0 rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-semibold animate-pulse">Fetching creator storefront...</p>
          </div>
        ) : creator ? (
          <>
        <div className="bg-white mx-4 md:mx-0 md:rounded-2xl shadow-sm border md:border border-gray-200 p-6 md:p-8 mb-6 flex flex-col md:flex-row items-center gap-6 md:gap-8 relative">
          
          {/* 1. Left: Avatar */}
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-blue-50 border-4 border-white shadow-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
            {creator.avatarUrl ? (
              <img src={creator.avatarUrl} alt={creator.displayName} className="w-full h-full object-cover" />
            ) : (
              <User className="w-12 h-12 text-blue-300" />
            )}
          </div>

          {/* 2. Middle: Bio Info */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex flex-col md:flex-row items-center md:justify-start gap-3 md:gap-4 mb-1">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{creator.displayName}</h2>
              
            {/* SUBSCRIBE BUTTON (Existing) */}
            {auth.currentUser?.uid !== uid && (
              <div className="flex gap-2"> {/* Added wrapper for spacing */}
                <button
                  onClick={handleSubscribe}
                  disabled={isSubmitting}
                  className={`group flex items-center justify-center gap-1.5 w-28 md:w-32 py-1.5 rounded-full text-xs md:text-sm font-bold transition-all shadow-sm ${
                    isSubscribed
                      ? "bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-600 border border-gray-200 hover:border-red-200"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  <Bell className="w-3.5 h-3.5" />
                  {isSubscribed ? "Subscribed" : "Subscribe"}
                </button>

                {/* NEW SHARE & REFER BUTTON */}
              <button
                onClick={() => navigate('/ReferFriend', { 
                  state: { 
                    targetCreatorName: creator?.displayName,
                    targetCreatorEmail: creator?.email,
                    targetCreatorId: uid 
                  } 
                })}
                className="flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-xs md:text-sm font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-all shadow-sm"
              >
                <Share2 className="w-3.5 h-3.5" />
                <span>Refer</span>
              </button>
              </div>
            )}
            </div>

            <p className="text-blue-600 font-semibold text-sm md:text-base mb-3 uppercase tracking-wider">
              {creator.role} • <span className="text-gray-600">{subscriberCount} Subscribers</span>
            </p>
            
            <p className="text-gray-600 text-sm md:text-base mb-4 max-w-2xl mx-auto md:mx-0">
              {creator.bio || "This creator hasn't added a bio yet, but their worksheets speak for themselves!"}
            </p>

            <div className="flex flex-wrap justify-center md:justify-start gap-3 md:gap-6 text-xs md:text-sm text-gray-500 mt-2">
              {(creator.city || creator.school) && (
                <span className="flex items-center gap-1.5 font-medium">
                  <MapPin className="w-4 h-4 text-blue-500" /> 
                  {[creator.school, creator.city].filter(Boolean).join(', ')}
                </span>
              )}
              
              {(creator.supportedStandards?.length > 0 || creator.standard || creator.board) && (
                <span className="flex items-center gap-1.5 font-medium">
                  <BookOpen className="w-4 h-4 text-blue-500" /> 
                  {[
                    creator.supportedStandards?.length > 0 
                      ? `Std ${creator.supportedStandards.sort((a,b) => a - b).join(', ')}` 
                      : (creator.standard ? `Std ${creator.standard}` : null), 
                    creator.board
                  ].filter(Boolean).join(' • ')}
                </span>
              )}
            </div>
          </div>

{/* 3. Right Side Stats Container */}
          <div className="grid grid-cols-3 md:flex md:flex-row gap-2 md:gap-3 w-full md:w-auto shrink-0 mt-4 md:mt-0">
            
            {/* BLUE: UPLOADED LESSONS */}
            <div className="flex flex-col items-center justify-center bg-gradient-to-b from-blue-50 to-blue-100/50 rounded-xl md:rounded-2xl p-2 md:p-5 border border-blue-200 shadow-sm flex-1">
              <BookOpen className="w-5 h-5 md:w-10 md:h-10 text-blue-500 mb-1 md:mb-2" strokeWidth={2.5} />
              <span className="text-xl md:text-4xl font-black text-blue-700 tracking-tight">{lessonCount}</span>
              <span className="text-[8px] md:text-xs font-bold text-blue-600/80 uppercase tracking-widest mt-0.5 md:mt-1 text-center leading-tight">
                <span className="hidden md:inline">Uploaded </span>Lessons
              </span>
            </div>

            {/* YELLOW: UPLOADED WORKSHEETS */}
            <div className="flex flex-col items-center justify-center bg-gradient-to-b from-yellow-50 to-yellow-100/50 rounded-xl md:rounded-2xl p-2 md:p-5 border border-yellow-200 shadow-sm flex-1">
              <FilePlus className="w-5 h-5 md:w-10 md:h-10 text-yellow-500 mb-1 md:mb-2" strokeWidth={2.5} />
              <span className="text-xl md:text-4xl font-black text-yellow-700 tracking-tight">{worksheets.length}</span>
              <span className="text-[8px] md:text-xs font-bold text-yellow-600/80 uppercase tracking-widest mt-0.5 md:mt-1 text-center leading-tight">
                <span className="hidden md:inline">Uploaded </span>Worksheets
              </span>
            </div>

            {/* GREEN: TOTAL DOWNLOADS */}
            <div className="flex flex-col items-center justify-center bg-gradient-to-b from-green-50 to-green-100/50 rounded-xl md:rounded-2xl p-2 md:p-5 border border-green-200 shadow-sm flex-1">
              <Download className="w-5 h-5 md:w-10 md:h-10 text-green-500 mb-1 md:mb-2" strokeWidth={2.5} />
              <span className="text-xl md:text-4xl font-black text-green-700 tracking-tight">{totalDownloadsCount}</span>
              <span className="text-[8px] md:text-xs font-bold text-green-600/80 uppercase tracking-widest mt-0.5 md:mt-1 text-center leading-tight">
                <span className="hidden md:inline">Total </span>Downloads
              </span>
            </div>

          </div>
        </div>
        
{/*
        
        {isOwnerOrAdmin && (
          <div className="mx-4 md:mx-0 mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-4 md:p-6 shadow-sm relative overflow-hidden">
            
            <IndianRupee className="absolute -right-6 -bottom-8 w-40 h-40 text-emerald-600/5 pointer-events-none" />
            
            <div className="flex items-center justify-between mb-4 border-b border-emerald-200/50 pb-3 relative z-10">
              <h3 className="text-base md:text-lg font-bold text-emerald-800 uppercase tracking-wide flex items-center gap-2">
                <Wallet className="w-5 h-5" /> Creator Dashboard
              </h3>
              {auth.currentUser?.email === 'eascode.ltd@gmail.com' && (
                <span className="bg-emerald-600 text-white text-[9px] md:text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                  Creator View
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center relative z-10">
              
              <div className="md:col-span-3 grid grid-cols-3 gap-2 md:gap-6 text-center md:text-left">
                <div className="bg-white/60 p-3 rounded-xl border border-emerald-100">
                  <p className="text-[10px] md:text-xs font-bold text-emerald-600 uppercase tracking-wide">Lessons</p>
                  <p className="text-sm md:text-base font-semibold text-gray-800">{lessonCount}</p>
                </div>
                <div className="bg-white/60 p-3 rounded-xl border border-emerald-100">
                  <p className="text-[10px] md:text-xs font-bold text-emerald-600 uppercase tracking-wide">Worksheets</p>
                  <p className="text-sm md:text-base font-semibold text-gray-800">{worksheets.length} </p>
                </div>
                <div className="bg-white/60 p-3 rounded-xl border border-emerald-100">
                  <p className="text-[10px] md:text-xs font-bold text-emerald-600 uppercase tracking-wide">Downloads</p>
                  <p className="text-sm md:text-base font-semibold text-gray-800">{totalDownloadsCount}</p>
                </div>
              </div>

              
              <div className="md:col-span-1 bg-emerald-600 text-white rounded-xl p-4 shadow-md flex flex-col items-center justify-center h-full">
                <p className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-emerald-100 mb-1">Total</p>
                <div className="flex items-center text-2xl md:text-3xl font-black">
                  {estimatedEarnings}
                </div>
              </div>
            </div>


          </div>
        )}

        */}

        {/* CREATOR'S WORKSHEETS GRID */}
        <div className="px-4 md:px-0 mb-4">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 border-b pb-2">Resources by {creator.displayName}</h3>
        </div>

        {worksheets.length === 0 ? (
          <div className="bg-white p-8 text-center rounded-2xl border border-gray-100 mx-4 md:mx-0">
            <p className="text-gray-500">This creator hasn't uploaded any public resources yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 md:gap-4 bg-gray-100 md:bg-transparent px-4 md:px-0">
            {worksheets.map(form => (
              <div key={form.id} className="bg-white rounded-xl md:rounded-2xl shadow-md hover:shadow-xl transition overflow-hidden border border-gray-400 flex flex-col">
                {/* PREVIEW CONTAINER */}
                <div className="bg-gray-100 relative w-full h-[280px] md:h-[300px] overflow-hidden" onContextMenu={(e) => e.preventDefault()}>
                  {form.fileUrl?.toLowerCase().includes(".pdf") ? (
                    isMobile ? (
                      <iframe src={`https://docs.google.com/gview?url=${encodeURIComponent(form.fileUrl)}&embedded=true`} title="preview" loading="lazy" scrolling="yes" style={{ WebkitOverflowScrolling: 'touch', transform: 'scale(1.3)', transformOrigin: 'top center' }} className="absolute top-[-56px] left-0 w-full h-[calc(100%+56px)] border-none" />
                    ) : (
                      <iframe src={`${form.fileUrl}#toolbar=0&navpanes=0&scrollbar=1`} title="preview" loading="lazy" style={{ transform: 'scale(1.2)', transformOrigin: 'top center' }} className="absolute inset-0 w-full h-full border-none" />
                    )
                  ) : (
                    <img src={form.fileUrl} alt="preview" loading="lazy" className="absolute inset-0 object-cover w-full h-full transition-transform duration-500 hover:scale-110" style={{ transform: 'scale(1.15)' }} />
                  )}
                </div>

                {/* CARD DETAILS */}
                <div className="flex-1 flex flex-col justify-between">
                  <h3 className="px-2 md:px-3 pt-2 md:pt-3 font-bold text-gray-800 text-xs md:text-sm line-clamp-2" title={form.title}>{form.title}</h3>

                  <div className="mt-1 border-t border-gray-100">
                    <button onClick={() => setOpenId(openId === form.id ? null : form.id)} className="w-full flex items-center justify-between px-2 md:px-3 py-1.5 md:py-2 text-[10px] md:text-sm font-medium text-gray-700 hover:bg-gray-50">
                      <span>Details</span>
                      <ChevronDown className={`w-3 h-3 md:w-4 md:h-4 transition-transform duration-300 ${openId === form.id ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openId === form.id ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                      <div className="px-2 md:px-3 pb-2 pt-1 text-[9px] md:text-xs text-gray-600 space-y-1">
                        <p>Lesson: {form.lessonName}</p>
                        <p>Type: {form.type}</p>
                        <p>Standard: {form.standard}</p>
                        {form.uploadTime && <p>Uploaded: {form.uploadTime.toLocaleDateString()}</p>}
                      </div>
                    </div>
                  </div>

                  <div className="px-2 md:px-3 pb-1.5 pt-1 text-[9px] md:text-sm border-t border-gray-100">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-gray-600 font-semibold">Rating:</span>
                      <span className="font-bold text-gray-800">{form.avgRating?.toFixed(1) || '0.0'} ⭐</span>
                    </div>
                  </div>

                  <div className="px-1.5 md:px-3 pb-2.5 flex flex-wrap gap-1 md:gap-2 mt-1">
                    <button onClick={() => handleDownload(form)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-1.5 md:py-2 px-1 rounded-full text-[9px] md:text-xs font-bold transition-all">
                      Download
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        ) : (
          <div className="min-h-[50vh] flex flex-col justify-center items-center bg-gray-50 mx-4 md:mx-0 rounded-2xl border border-gray-200 p-8 text-center">
            <User className="w-16 h-16 text-gray-300 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Creator Not Found</h2>
            <p className="text-sm text-gray-500 mb-4">Select a creator from the directory above to view their storefront.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default CreatorProfile;